// 유틸리티 함수들

// 선형 보간
export function lerp(start, end, amt) {
    return (1 - amt) * start + amt * end;
}

// 노트의 BPM과 subdivision을 가져오는 함수
export function getNoteTimingParams(note, globalBpm = 120, globalSubdivisions = 16) {
    return {
        bpm: note.bpm || globalBpm,
        subdivisions: note.subdivisions || globalSubdivisions
    };
}

// 노트 타입을 외부 형식으로 변환
export function convertNoteTypeToExternal(type) {
    const typeMap = {
        "tab": "Tab",
        "direction": "Direction", 
        "both": "Both",
        "longtab": "LongTab",
        "longdirection": "LongDirection",
        "longboth": "LongBoth",
        "node": "Node"
    };
    return typeMap[type] || "Tab";
}

// 외부 형식을 노트 타입으로 변환
export function convertExternalToNoteType(external) {
    const typeMap = {
        "Tab": "tab",
        "Direction": "direction",
        "Both": "both", 
        "LongTab": "longtab",
        "LongDirection": "longdirection",
        "LongBoth": "longboth",
        "Node": "node"
    };
    return typeMap[external] || "tab";
}

// Long Note의 시간을 계산하는 함수
export function calculateLongNoteTime(note, globalBpm, globalSubdivisions) {
    if (!note.longTime || note.longTime <= 0) return 0;
    
    const timing = getNoteTimingParams(note, globalBpm, globalSubdivisions);
    return beatToTime(note.longTime, timing.bpm, timing.subdivisions);
}

// pathBeat 계산 함수 (_sectionOffset 지원)
export function calculatePathBeat(note, preDelaySeconds, globalBpm, globalSubdivisions) {
    const sectionOffset = note._sectionOffset || 0;
    if (note.beat === 0 && note.type === "direction" && sectionOffset === 0) {
        return 0;
    } else {
        const timing = getNoteTimingParams(note, globalBpm, globalSubdivisions);
        const originalTime = sectionOffset + beatToTime(note.beat, timing.bpm, timing.subdivisions);
        const adjustedTime = originalTime + preDelaySeconds;
        return timeToBeat(adjustedTime, globalBpm, globalSubdivisions);
    }
}

// 구간 번호(sectionIndex)를 각 노트에 부여 (배열 순서 기준)
// beatReset=true인 노트를 만날 때마다 구간 번호 1 증가
// 이 함수는 배열 순서가 변경될 때마다 호출해야 함 (노트 추가/삭제/정렬/beatReset 변경 시)
export function recomputeSectionIndices(notes) {
    let currentSection = 0;
    for (let i = 0; i < notes.length; i++) {
        notes[i].sectionIndex = currentSection;
        if (notes[i].beatReset) {
            currentSection++;
        }
    }
}

// 구간(section) 오프셋 계산 함수
// sectionIndex가 설정된 경우: 배열 순서에 독립적인 방식으로 계산 (정렬 안정성 보장)
// sectionIndex 미설정: 배열 순서 기반 계산 (레거시 폴백)
// offsets[i] = i번 노트의 구간 시작 절대시간(초)
export function calculateSectionOffsets(notes, globalBpm, globalSubdivisions) {
    if (notes.length === 0) return [];

    // sectionIndex 기반 계산 (정렬 이후에도 안정적)
    if (notes[0].sectionIndex !== undefined) {
        // 각 구간의 시작 절대시간을 beatReset 노트에서 계산
        const sectionStartTimes = new Map([[0, 0]]);

        for (const note of notes) {
            if (note.beatReset) {
                const sIdx = note.sectionIndex;
                if (!sectionStartTimes.has(sIdx + 1)) {
                    const prevOffset = sectionStartTimes.get(sIdx) || 0;
                    const bpm = note.bpm || globalBpm;
                    const subs = note.subdivisions || globalSubdivisions;
                    sectionStartTimes.set(sIdx + 1, prevOffset + beatToTime(note.beat, bpm, subs));
                }
            }
        }

        return notes.map(n => sectionStartTimes.get(n.sectionIndex) || 0);
    }

    // 레거시 폴백: 배열 순서 기반 계산
    const offsets = new Array(notes.length).fill(0);
    let currentOffset = 0;
    for (let i = 0; i < notes.length; i++) {
        offsets[i] = currentOffset;
        const note = notes[i];
        if (note.beatReset) {
            const bpm = note.bpm || globalBpm;
            const subs = note.subdivisions || globalSubdivisions;
            currentOffset += beatToTime(note.beat, bpm, subs);
        }
    }
    return offsets;
}

// 시간을 비트로 변환
export function timeToBeat(time, bpm, subdivisions) {
    const beatDuration = 60 / bpm;
    const subdivisionDuration = beatDuration / subdivisions;
    return time / subdivisionDuration;
}

// 비트를 시간으로 변환
export function beatToTime(beat, bpm, subdivisions) {
    const beatDuration = 60 / bpm;
    const subdivisionDuration = beatDuration / subdivisions;
    return beat * subdivisionDuration;
}

// 방향을 벡터로 변환
export function directionToVector(direction) {
    const directions = {
        "up": [0, -1],
        "down": [0, 1],
        "left": [-1, 0],
        "right": [1, 0],
        "upleft": [-1, -1],
        "upright": [1, -1],
        "downleft": [-1, 1],
        "downright": [1, 1]
    };
    return directions[direction] || [0, 0];
}

// angleOffset 상한(도). 45도 대각 기준 ±15까지는 플레이어가 그 방향의 대각선으로 인지한다.
export const MAX_ANGLE_OFFSET = 15;

// 범위 밖 값이나 쓰레기 값이 경로 계산에 흘러들지 않게 막는다.
export function clampAngleOffset(value) {
    const n = Number(value);
    if (!Number.isFinite(n)) return 0;
    return Math.max(-MAX_ANGLE_OFFSET, Math.min(MAX_ANGLE_OFFSET, n));
}

// 경로 노트의 진행 방향/각도를 순차 캐리로 해석한다.
// direction 미지정(none)이면 직전 값을 상속하고, 명시되면 새 선언으로 보아 angleOffset도 리셋한다.
// direction과 angleOffset은 항상 한 쌍으로 갱신된다.
// Unity TrackGenerator.CreateNodeList()와 동일한 규칙 — 갈리면 경로가 갈린다.
export function resolvePathHeadings(pathDirectionNotes) {
    let lastDirection = "right";
    let lastAngleOffset = 0;

    const first = pathDirectionNotes[0];
    if (first) {
        if (first.direction && first.direction !== "none") {
            lastDirection = first.direction;
        }
        lastAngleOffset = first.angleOffset || 0;
    }

    return pathDirectionNotes.map(note => {
        if (note.direction && note.direction !== "none") {
            lastDirection = note.direction;
            lastAngleOffset = note.angleOffset || 0;
        }
        return { direction: lastDirection, angleOffset: lastAngleOffset };
    });
}

// direction + angleOffset을 단위 벡터로. angleOffset은 CCW 양수, 도 단위, ±15 clamp.
// offset이 0이면 삼각함수를 타지 않는다 — 기존 차트가 부동소수점 오차 없이 그대로 재현되어야 한다.
// 회전각을 뒤집는 이유: 이 에디터는 화면 좌표계라 Y축이 아래로 향한다(up = [0,-1]).
// Unity는 Y축이 위(Up = (0,1))라, 같은 회전 행렬을 쓰면 화면상 반대로 돈다.
export function directionToVectorWithAngle(direction, angleOffset) {
    const [dx, dy] = directionToVector(direction);
    if (!angleOffset) return [dx, dy];

    const rad = -clampAngleOffset(angleOffset) * Math.PI / 180;
    const cos = Math.cos(rad), sin = Math.sin(rad);
    return [dx * cos - dy * sin, dx * sin + dy * cos];
}

// 곡선 프리셋 — 자주 쓰는 curvature/bias 조합. 우클릭 컨텍스트 메뉴에서 원클릭 적용.
// curvature: 양수 = 반시계(화면 기준 좌커브), 음수 = 시계(우커브). bias: 양수 = 앞당김(초반이 급),
// 음수 = 뒤당김(후반이 급). 값이 (0,0)이면 곡선 제거(직선). 소프트리밋 ±0.5 안에서 고른다.
export const CURVE_PRESETS = [
    { label: "직선",            curvature: 0.0,  bias: 0.0  },
    { label: "좌 커브 (약)",     curvature: 0.2,  bias: 0.0  },
    { label: "좌 커브 (강)",     curvature: 0.4,  bias: 0.0  },
    { label: "우 커브 (약)",     curvature: -0.2, bias: 0.0  },
    { label: "우 커브 (강)",     curvature: -0.4, bias: 0.0  },
    { label: "좌 커브·앞당김",   curvature: 0.3,  bias: 0.2  },
    { label: "좌 커브·뒤당김",   curvature: 0.3,  bias: -0.2 },
    { label: "우 커브·앞당김",   curvature: -0.3, bias: 0.2  },
    { label: "우 커브·뒤당김",   curvature: -0.3, bias: -0.2 },
];

// JSON의 bezier 필드를 방어적으로 정규화한다.
// 값이 없거나 기본값(curvature=0, bias=0)이면 null을 반환해 노트에 필드를 달지 않는다 —
// angleOffset과 같은 처우로, 기본값이면 필드 부재로 남아 기존 차트가 무수정 재현된다.
// curvature는 소프트 가이드라 clamp하지 않는다(§3-5/Q2). NaN/±Inf 같은 쓰레기만 걸러낸다.
export function normalizeBezier(bezier) {
    if (!bezier || typeof bezier !== "object") return null;
    const c = Number(bezier.curvature);
    const b = Number(bezier.bias);
    const curvature = Number.isFinite(c) ? c : 0;
    const bias = Number.isFinite(b) ? b : 0;
    if (!curvature && !bias) return null;
    return { curvature, bias };
}

// curvature 권장 상한. §3-5/Q2: 하드 제약이 아니라 소프트 가이드다.
// |curvature|가 이 값을 넘으면 곡선이 과해져 끝점이 크게 당겨지므로 경고만 낸다(입력은 허용).
export const CURVATURE_SOFT_LIMIT = 0.5;

// --- 베지어 곡선 (track-path-curve §2.3) ---
//
// 아래 상수는 Unity 클라와 합의한 규범(MUST)이다. 양측이 각자 구현하되 같은 값을 내야 하므로
// 임의로 바꾸면 에디터 프리뷰와 실제 게임 경로가 갈린다. 성능 튜닝 대상이 아니다.
const BEZIER_SEARCH_ITERATIONS = 20;   // 조기 종료 금지 — 반복 횟수까지 양측이 같아야 한다
const BEZIER_ARC_SAMPLES = 32;         // 균등 t 분할

// 2차 베지어 위 한 점. P0=시작, P1=제어점, P2=끝점.
function bezierPoint(p0, p1, p2, t) {
    const it = 1 - t;
    return {
        x: it * it * p0.x + 2 * it * t * p1.x + t * t * p2.x,
        y: it * it * p0.y + 2 * it * t * p1.y + t * t * p2.y
    };
}

// 끝점 거리 r일 때의 곡선 제어점. u/n은 반드시 단위 벡터여야 한다 —
// 길이가 1이 아니면 bias/curvature 항이 그만큼 부풀어 대각 구간만 어긋난다.
function bezierControlPoints(start, u, n, r, curvature, bias) {
    const p1 = {
        x: start.x + u.x * (r * (0.5 + bias)) + n.x * (curvature * r),
        y: start.y + u.y * (r * (0.5 + bias)) + n.y * (curvature * r)
    };
    const p2 = { x: start.x + u.x * r, y: start.y + u.y * r };
    return { p1, p2 };
}

// 곡선 호장 길이 — 균등 t 분할 샘플의 누적 유클리드 거리로 근사.
function bezierArcLength(p0, p1, p2) {
    let length = 0;
    let prev = p0;
    for (let i = 1; i <= BEZIER_ARC_SAMPLES; i++) {
        const cur = bezierPoint(p0, p1, p2, i / BEZIER_ARC_SAMPLES);
        length += Math.hypot(cur.x - prev.x, cur.y - prev.y);
        prev = cur;
    }
    return length;
}

// 구간의 곡선을 푼다. 불변식은 끝점이 아니라 "호장 길이 L"이다.
// 곡선을 부풀린 만큼 끝점을 앞으로 당겨(r* < L) 호장이 정확히 L이 되게 한다.
// 반환: { end, p0, p1, p2, r } — end가 다음 구간의 시작점이 된다(순차 누적).
export function solveCurveSegment(start, direction, angleOffset, distance, bezier) {
    const [rawX, rawY] = directionToVectorWithAngle(direction, angleOffset);
    const mag = Math.hypot(rawX, rawY) || 1;
    const u = { x: rawX / mag, y: rawY / mag };

    const curvature = bezier?.curvature || 0;
    const bias = bezier?.bias || 0;

    // 직선은 이분 탐색을 태우지 않는다. 태우면 r*이 L에 1e-6 수준으로만 수렴해
    // 기존 차트가 비트 단위로 재현되지 않는다. 최적화가 아니라 정확성 장치다.
    if (!curvature) {
        const end = { x: start.x + u.x * distance, y: start.y + u.y * distance };
        return { end, p0: start, p1: null, p2: end, r: distance };
    }

    // 화면 좌표계(Y축 아래)에서 CCW 법선. Unity의 n=(-u.y, u.x)와 같은 물리 방향을 가리킨다.
    const n = { x: u.y, y: -u.x };

    // 호장은 r에 대해 단조 증가하므로 이분 탐색으로 유일해를 얻는다.
    let lo = 0, hi = distance, r = distance;
    for (let i = 0; i < BEZIER_SEARCH_ITERATIONS; i++) {
        r = (lo + hi) / 2;
        const { p1, p2 } = bezierControlPoints(start, u, n, r, curvature, bias);
        if (bezierArcLength(start, p1, p2) < distance) lo = r; else hi = r;
    }

    const { p1, p2 } = bezierControlPoints(start, u, n, r, curvature, bias);
    return { end: p2, p0: start, p1, p2, r };
}

// 곡선 위를 등속으로 달리기 위한 호장 LUT.
// 베지어의 t는 호장에 비례하지 않아서, 거리 진행도를 t로 직접 쓰면
// 구간 전체 길이가 맞아도 구간 "안"에서 캐릭터가 출렁인다.
export function buildArcLengthLUT(p0, p1, p2) {
    const cumulative = [0];
    let prev = p0;
    for (let i = 1; i <= BEZIER_ARC_SAMPLES; i++) {
        const cur = bezierPoint(p0, p1, p2, i / BEZIER_ARC_SAMPLES);
        cumulative.push(cumulative[i - 1] + Math.hypot(cur.x - prev.x, cur.y - prev.y));
        prev = cur;
    }
    return cumulative;
}

// 거리 진행도 s01(0~1)에 해당하는 곡선 위 점. LUT를 역참조해 등속을 보장한다.
export function pointAtArcProgress(p0, p1, p2, lut, s01) {
    const total = lut[lut.length - 1];
    if (!total) return { ...p0 };

    const target = Math.max(0, Math.min(1, s01)) * total;
    let i = 1;
    while (i < lut.length && lut[i] < target) i++;

    const segLen = lut[i] - lut[i - 1];
    const frac = segLen > 0 ? (target - lut[i - 1]) / segLen : 0;
    const t = (i - 1 + frac) / BEZIER_ARC_SAMPLES;
    return bezierPoint(p0, p1, p2, t);
}

// Pre-delay 초 단위로 가져오기
export function getPreDelaySeconds() {
    const preDelayMs = parseInt(document.getElementById("pre-delay").value || 0);
    return preDelayMs / 1000;
}

// Unity 게임 좌표계 변환 함수들
// Unity 공식: distance = deltaTime × multiplierConstant × bpm
// multiplierConstant = 8 × 배속 × 0.05 = 0.4 × 배속

// Unity에서 1비트 동안의 이동거리 계산 (정확한 공식)
export function calculateUnityMovementPerBeat(bpm, speedMultiplier = 1.0) {
    // 1비트 = 60초 / (BPM × subdivisions/4)
    // 기본 subdivisions = 16이므로 4분음표 기준
    const beatDuration = 60 / bpm; // 1beat의 시간 (초)
    const multiplierConstant = 0.4 * speedMultiplier; // 8 × 배속 × 0.05

    // Unity 공식: distance = deltaTime × multiplierConstant × bpm
    return beatDuration * multiplierConstant * bpm;
}

// Unity 월드 좌표에서 노드 간 거리 계산
export function calculateUnityNodeDistance(deltaTime, bpm, speedMultiplier = 1.0) {
    const multiplierConstant = 0.4 * speedMultiplier;
    return deltaTime * multiplierConstant * bpm;
}

// 에디터 좌표를 Unity 월드 좌표로 변환
export function convertEditorToUnityCoordinate(editorDistance, bpm, speedMultiplier = 1.0) {
    const unityMovementPerBeat = calculateUnityMovementPerBeat(bpm, speedMultiplier);
    const editorMovementPerBeat = 24 * speedMultiplier; // 에디터의 현재 공식

    // 변환 비율
    const ratio = unityMovementPerBeat / editorMovementPerBeat;
    return editorDistance * ratio;
}

// Unity 월드 좌표를 에디터 좌표로 변환
export function convertUnityToEditorCoordinate(unityDistance, bpm, speedMultiplier = 1.0) {
    const unityMovementPerBeat = calculateUnityMovementPerBeat(bpm, speedMultiplier);
    const editorMovementPerBeat = 24 * speedMultiplier; // 에디터의 현재 공식

    // 변환 비율
    const ratio = editorMovementPerBeat / unityMovementPerBeat;
    return unityDistance * ratio;
}

// Unity에서 노트의 정확한 위치 계산 (Lerp 방식)
export function calculateUnityNotePosition(noteTime, previousNodeTime, nextNodeTime,
    previousNodePosition, nextNodePosition) {

    if (nextNodeTime === previousNodeTime) {
        return { ...previousNodePosition };
    }

    const t = (noteTime - previousNodeTime) / (nextNodeTime - previousNodeTime);
    const clampedT = Math.max(0, Math.min(1, t));

    return {
        x: previousNodePosition.x + (nextNodePosition.x - previousNodePosition.x) * clampedT,
        y: previousNodePosition.y + (nextNodePosition.y - previousNodePosition.y) * clampedT
    };
}

// Unity 방향 벡터 정규화
export function normalizeDirection(direction) {
    const [dx, dy] = directionToVector(direction);
    const magnitude = Math.hypot(dx, dy);

    if (magnitude === 0) return [0, 0];

    return [dx / magnitude, dy / magnitude];
}

// BPM fade를 고려한 진행도 계산 (Unity StageUtils.CalculateFadeProgress와 동일)
// normalizedTime: 0~1 사이의 시간 진행도
// startBpm: 시작 BPM, endBpm: 끝 BPM
// 반환값: 실제 이동 거리 진행도 (0~1)
export function calculateFadeProgress(normalizedTime, startBpm, endBpm) {
    // v(t) = startBpm + (endBpm - startBpm) * t (선형 BPM 변화)
    // 거리 = ∫v(t)dt = startBpm*t + (endBpm - startBpm)*t²/2
    // 정규화된 총 거리 = startBpm + (endBpm - startBpm)/2 = (startBpm + endBpm)/2

    const t = normalizedTime;

    // 전체 구간의 총 거리 (t=1일 때)
    const totalDistance = (startBpm + endBpm) / 2;

    // 방어 코드: totalDistance가 0이면 선형 보간으로 fallback
    if (Math.abs(totalDistance) < 0.001) {
        return t;
    }

    // 현재 시점까지의 누적 거리
    const currentDistance = startBpm * t + (endBpm - startBpm) * t * t / 2;

    // 거리 진행도 반환
    return currentDistance / totalDistance;
}